import React, { useState, useRef, useCallback, useEffect } from 'react'
import { useMapsLibrary, APIProvider } from '@vis.gl/react-google-maps'
import { TextField, List, ListItem, ListItemText, Paper, Box } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import { OnSelect } from './types'

const useStyles = makeStyles(theme => ({
  container: {
    position: 'relative',
    width: '100%',
  },
  suggestionsContainer: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 1000,
    maxHeight: '300px',
    overflowY: 'auto',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '4px',
    boxShadow: theme.shadows[4],
  },
  suggestionItem: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  suggestionItemActive: {
    backgroundColor: theme.palette.action.selected,
  },
}))

type Props = {
  onSelect: OnSelect
  placeholder?: string
  required?: boolean
  disabled?: boolean
  value?: string
  onChange?: (value: string) => void
  error?: boolean
  helperText?: string
  label?: string
}

const GooglePlacesAutocompleteV2Inner: React.FC<Props> = ({
  onSelect,
  placeholder = 'Buscar dirección...',
  required = false,
  disabled = false,
  value = '',
  onChange,
  error = false,
  helperText,
  label = 'Dirección',
}) => {
  const classes = useStyles()
  const places = useMapsLibrary('places')
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null)
  const placesService = useRef<google.maps.places.PlacesService | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const mapDivRef = useRef<HTMLDivElement>(null)

  const [inputValue, setInputValue] = useState(value)
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  useEffect(() => {
    if (!places) return

    autocompleteService.current = new google.maps.places.AutocompleteService()

    if (mapDivRef.current) {
      placesService.current = new google.maps.places.PlacesService(mapDivRef.current)
    }
  }, [places])

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value
      setInputValue(newValue)
      setActiveIndex(-1)

      if (!isOpen) {
        onChange?.(newValue)
      }

      if (newValue.length > 2) {
        autocompleteService.current?.getPlacePredictions(
          {
            input: newValue,
            componentRestrictions: { country: ['ar', 'uy', 'cl', 'mx'] },
            types: ['address'],
          },
          (predictions, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
              setSuggestions(predictions)
              setIsOpen(true)
            } else {
              setSuggestions([])
              setIsOpen(false)
            }
          },
        )
      } else {
        setSuggestions([])
        setIsOpen(false)
      }
    },
    [isOpen, onChange],
  )

  const handleSuggestionSelect = useCallback(
    (prediction: google.maps.places.AutocompletePrediction) => {
      if (!placesService.current) return

      placesService.current.getDetails(
        {
          placeId: prediction.place_id,
          fields: ['formatted_address', 'geometry', 'name', 'vicinity'],
        },
        (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place) {
            const location = place.geometry?.location

            if (location) {
              const lat = location.lat()
              const lng = location.lng()

              const shortAddress = prediction.description || place.formatted_address || ''

              const cleanAddress = shortAddress
                .replace(/Ciudad Autónoma de Buenos Aires/g, 'CABA')
                .replace(/, Argentina$/, '')

              setInputValue(cleanAddress)
              setIsOpen(false)
              setSuggestions([])
              onChange?.(cleanAddress)
              onSelect({ lat, lng, address: cleanAddress })
            }
          }
        },
      )
    },
    [onSelect, onChange],
  )

  useEffect(() => {
    setInputValue(value)
  }, [value])

  return (
    <Box className={classes.container}>
      <div ref={mapDivRef} style={{ display: 'none' }} />

      <TextField
        ref={inputRef}
        fullWidth
        label={label}
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => suggestions.length > 0 && setIsOpen(true)}
        required={required}
        disabled={disabled}
        error={error}
        helperText={helperText}
        autoComplete="off"
      />

      {isOpen && suggestions.length > 0 && (
        <Paper className={classes.suggestionsContainer}>
          <List dense>
            {suggestions.map((suggestion, index) => (
              <ListItem
                key={suggestion.place_id}
                className={`${classes.suggestionItem} ${
                  index === activeIndex ? classes.suggestionItemActive : ''
                }`}
                onClick={() => handleSuggestionSelect(suggestion)}
              >
                <ListItemText
                  primary={suggestion.structured_formatting.main_text}
                  secondary={suggestion.structured_formatting.secondary_text}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  )
}

GooglePlacesAutocompleteV2Inner.defaultProps = {
  placeholder: 'Buscar dirección...',
  required: false,
  disabled: false,
  value: '',
  onChange: undefined,
  error: false,
  helperText: undefined,
  label: 'Dirección',
}

const GooglePlacesAutocompleteV2: React.FC<Props> = props => (
  <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string} libraries={['places']}>
    <GooglePlacesAutocompleteV2Inner {...props} />
  </APIProvider>
)

GooglePlacesAutocompleteV2.defaultProps = {
  placeholder: 'Buscar dirección...',
  required: false,
  disabled: false,
  value: '',
  onChange: undefined,
  error: false,
  helperText: undefined,
  label: 'Dirección',
}

export { GooglePlacesAutocompleteV2 }
